import type { JSX } from "react";

interface ContextBarProps {
  /** Shown only when automatic token renewal has failed — otherwise the bar stays quiet. */
  renewalFailed: boolean;
  /** Who is signed in, where somebody is. Their name belongs on screen: it is the answer to
   * "whose access am I looking at", and under Keycloak there is no picker to infer it from. */
  tester: { name: string; roles: string[] } | null;
  /** Sign out through the identity provider rather than just forgetting the local session.
   * Absent where the bench mints its own tokens: there is no sign-in to undo, and identity is
   * switched in the Identities panel instead. */
  signOutHref: string | null;
  benchVersion: string;
}

/**
 * The title strip. Deliberately almost empty.
 *
 * It used to repeat the environment, tenant, project and agent, and carry an identity dropdown
 * and a token countdown with a Renew button. Every one of those was either a duplicate of the
 * Scope panel directly beneath it or a chore the bench can do itself: scope is edited where it is
 * shown, identity is chosen in the Identities panel, and the token renews on a timer. What is
 * left is the name and the way out.
 *
 * The one thing that can appear here is a renewal failure, because that is the only session
 * problem a tester has to act on and it has no other home.
 */
export function ContextBar({
  renewalFailed,
  tester,
  signOutHref,
  benchVersion,
}: ContextBarProps): JSX.Element {
  return (
    <header className="context-bar">
      <div className="context-bar__inner">
        <div className="context-bar__brand" title={`Memory Test Bench v${benchVersion}`}>
          <strong>Memory Test Bench</strong>
        </div>

        {renewalFailed && (
          <span
            className="flag flag--danger"
            title="The bench could not renew the token. Calls will start failing with 401."
          >
            token renewal failing
          </span>
        )}

        {tester && (
          <span className="context-bar__who" title={`Roles: ${tester.roles.join(", ") || "none"}`}>
            {tester.name}
          </span>
        )}

        {signOutHref && (
          // A navigation, because signing out has to visit the identity provider to end its
          // session as well -- otherwise the next sign-in skips the login form.
          <a className="button" href={signOutHref}>
            Sign out
          </a>
        )}
      </div>
    </header>
  );
}
